import { useState, useCallback, useRef, useEffect } from 'react'
import { useAssessmentStore } from '../../store/assessmentStore'
import { useAvatar } from '../Avatar'
import { useSpeechQueueStore } from '../../store/speechQueueStore'
import Webcam from '../Webcam'
import { initFaceDetector, enrollFaceFromBase64, verifyFaceFromBase64, isDetectorReady } from '../../services/faceDetection'
import { enterFullscreen } from '../../utils/fullscreen'
import { getProctoringConfig } from '../../store/proctoringConfigStore'
import {
  runLivenessCheck,
  getRandomChallenge,
  getChallengeInstruction,
  getChallengeLabel,
  initLivenessDetector,
  type LivenessChallenge,
  type LivenessResult,
} from '../../services/livenessDetection'

// Enrollment phases
type EnrollmentPhase = 'intro' | 'liveness' | 'capturing' | 'enrolling' | 'verifying' | 'complete' | 'failed'

const LIVENESS_COUNTDOWN_SECONDS = 3

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function findActiveVideo(): HTMLVideoElement | null {
  const videos = document.querySelectorAll('video')
  for (const video of videos) {
    if (video.srcObject && video.videoWidth > 0) {
      return video
    }
  }
  return null
}

export default function FaceEnrollmentStep() {
  const { learner, sessionToken, updateLearner, setFaceEnrolled, setFaceEncodingId, setStep } = useAssessmentStore()
  const { speak, speakAndWait, clearQueue } = useAvatar()
  const { isSpeaking } = useSpeechQueueStore()

  const [phase, setPhase] = useState<EnrollmentPhase>('intro')
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [isLoadingModel, setIsLoadingModel] = useState(true)
  const [enrollmentStatus, setEnrollmentStatus] = useState<{
    success: boolean
    message: string
  } | null>(null)

  // Liveness detection state
  const [livenessChallenge, setLivenessChallenge] = useState<LivenessChallenge | null>(null)
  const [livenessProgress, setLivenessProgress] = useState(0)
  const [livenessResult, setLivenessResult] = useState<LivenessResult | null>(null)
  const [livenessAttempts, setLivenessAttempts] = useState(0)
  const [introReady, setIntroReady] = useState(false)
  const [countdown, setCountdown] = useState<number | null>(null)
  const [isRunningLiveness, setIsRunningLiveness] = useState(false)
  const [isStartingAssessment, setIsStartingAssessment] = useState(false)

  // Refs
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const greeted = useRef(false)
  const enrollmentStarted = useRef(false)

  // Max liveness attempts before allowing skip
  const MAX_LIVENESS_ATTEMPTS = 3

  // Initialize face detector and liveness detector on mount
  useEffect(() => {
    const loadDetectors = async () => {
      try {
        await Promise.all([
          initFaceDetector(),
          initLivenessDetector(),
        ])
      } finally {
        setIsLoadingModel(false)
      }
    }
    loadDetectors()
  }, [])

  // Speak intro when step loads and model is ready — enable actions only after Stella finishes
  useEffect(() => {
    if (!greeted.current && !isLoadingModel) {
      greeted.current = true
      setIntroReady(false)
      void (async () => {
        await waitMs(500)
        await speakAndWait(
          "Now let's verify you're a real person and set up face recognition. This ensures the integrity of your assessment. I'll ask you to perform a simple action, then we'll capture your photo. When I'm done speaking, follow the on-screen instructions and press Begin Verification."
        )
        setIntroReady(true)
      })()
    }
  }, [speakAndWait, isLoadingModel])

  // Handle webcam stream ready
  const handleStream = useCallback((stream: MediaStream) => {
    streamRef.current = stream
    // Get video element from stream
    const videoTracks = stream.getVideoTracks()
    if (videoTracks.length > 0) {
      // Find the video element that's using this stream
      const videos = document.querySelectorAll('video')
      videos.forEach(video => {
        if (video.srcObject === stream) {
          videoRef.current = video
        }
      })
    }
  }, [])

  // Start liveness check (after Stella finishes + optional countdown)
  const startLivenessCheck = useCallback(async () => {
    if (isRunningLiveness || isSpeaking) return

    const challenge = getRandomChallenge()
    setLivenessChallenge(challenge)
    setLivenessProgress(0)
    setLivenessResult(null)
    setPhase('liveness')
    setIsRunningLiveness(true)

    const instruction = getChallengeInstruction(challenge)
    await speakAndWait(
      `Let's verify you're a live person. ${instruction} I'll start watching in a moment — get ready.`
    )

    // Countdown so capture begins only after Stella is done talking
    for (let i = LIVENESS_COUNTDOWN_SECONDS; i >= 1; i--) {
      setCountdown(i)
      await waitMs(1000)
    }
    setCountdown(null)

    const videoElement = findActiveVideo()
    if (!videoElement) {
      setIsRunningLiveness(false)
      setLivenessResult({
        passed: false,
        confidence: 0,
        challenge,
        error: 'Camera not ready. Please ensure your camera is active.',
      })
      setPhase('failed')
      speak("I couldn't access your camera. Please refresh and try again.")
      return
    }

    try {
      const result = await runLivenessCheck(
        videoElement,
        challenge,
        5000,
        setLivenessProgress
      )

      setLivenessResult(result)
      setLivenessAttempts((prev) => prev + 1)

      if (result.passed) {
        // Unblock capture UI immediately — don't wait for Stella to finish talking
        setPhase('capturing')
        speak('Great, I detected your action. Now position your face in the oval and press Capture Photo when you are ready.')
      } else {
        if (livenessAttempts + 1 >= MAX_LIVENESS_ATTEMPTS) {
          speak("I'm having trouble detecting your action. You can try again or skip this step and proceed to photo capture.")
        } else {
          speak(`I couldn't clearly detect your action. Let's try again. ${instruction}`)
        }
        setPhase('failed')
      }
    } catch (error) {
      console.error('[FaceEnrollment] Liveness check error:', error)
      setLivenessResult({
        passed: false,
        confidence: 0,
        challenge,
        error: error instanceof Error ? error.message : 'Liveness check failed',
      })
      setLivenessAttempts((prev) => prev + 1)
      setPhase('failed')
      speak('Something went wrong with the liveness check. Please try again.')
    } finally {
      setIsRunningLiveness(false)
      setCountdown(null)
    }
  }, [speak, speakAndWait, isSpeaking, isRunningLiveness, livenessAttempts])


  // Skip liveness and proceed to capture
  const skipLiveness = useCallback(() => {
    speak("Okay, let's proceed to photo capture. Position your face in the oval and press the capture button.")
    setPhase('capturing')
  }, [speak])

  // Enroll face using session-based API
  const enrollFace = useCallback(async (imageBase64: string) => {
    if (!imageBase64 || !learner || !sessionToken) {
      speak("Session error. Please start over.")
      setPhase('failed')
      return
    }

    setPhase('enrolling')
    speak('Processing your photo.')

    try {
      const result = await enrollFaceFromBase64(imageBase64, sessionToken)

      if (result.success && result.encodingId) {
        // Store encoding ID for verification during assessment
        setFaceEncodingId(result.encodingId)
        updateLearner({
          faceEncodingId: result.encodingId,
          enrolledPhotoUrl: `data:image/jpeg;base64,${imageBase64}`,
        })
        setFaceEnrolled(true)

        setPhase('verifying')
        await waitMs(500)

        const verifyResult = await verifyFaceFromBase64(imageBase64, result.encodingId, sessionToken || undefined)

        console.log('[FaceEnrollment] Verification test result:', verifyResult)

        if (verifyResult.verified || (verifyResult.similarity && verifyResult.similarity >= 25)) {
          setEnrollmentStatus({
            success: true,
            message: `Identity verified successfully! (${Math.round(verifyResult.similarity || 0)}% match)`,
          })
          setPhase('complete')
          speak("Your identity has been verified. Click Start Assessment when you're ready.")
        } else if (verifyResult.error === 'encoding_expired' || verifyResult.error === 'no_enrolled_face') {
          setEnrollmentStatus({
            success: false,
            message: 'Face API connection issue. Please try again.',
          })
          speak("There was a problem with the face verification system. Please try capturing your photo again.")
          setPhase('failed')
          enrollmentStarted.current = false
        } else if (verifyResult.facesDetected === 0) {
          setEnrollmentStatus({
            success: false,
            message: 'Could not verify face. Please try again with better lighting.',
          })
          speak("I couldn't verify your face. Please try again with better lighting.")
          setPhase('failed')
          enrollmentStarted.current = false
        } else {
          // Low similarity but face was detected - API might be having issues
          const similarity = verifyResult.similarity || 0
          if (similarity < 15) {
            setEnrollmentStatus({
              success: false,
              message: `Verification quality too low (${Math.round(similarity)}%). Please try again.`,
            })
            speak("The verification quality is too low. Please make sure you're facing the camera directly and try again.")
            setPhase('failed')
            enrollmentStarted.current = false
          } else {
            // Acceptable but not great
            setEnrollmentStatus({
              success: true,
              message: `Identity verified (${Math.round(similarity)}% match). Good enough to proceed.`,
            })
            setPhase('complete')
            speak("Your identity has been verified. Click Start Assessment when you're ready.")
          }
        }
      } else {
        setEnrollmentStatus({
          success: false,
          message: result.error || 'Face detection failed. Please try again.',
        })
        speak("I couldn't detect your face clearly. Please make sure your face is well-lit and centered, then try again.")
        setPhase('failed')
        enrollmentStarted.current = false
      }
    } catch (error) {
      void error
      setEnrollmentStatus({
        success: false,
        message: 'Face detection failed. Please try again.',
      })
      speak("Something went wrong. Please try again.")
      setPhase('failed')
      enrollmentStarted.current = false
    }
  }, [learner, sessionToken, setFaceEncodingId, updateLearner, setFaceEnrolled, speak])

  // Handle face capture - start enrollment
  const handleCapture = useCallback((imageBase64: string) => {
    if (enrollmentStarted.current) return
    enrollmentStarted.current = true
    setCapturedImage(imageBase64)
    enrollFace(imageBase64)
  }, [enrollFace])

  // Start assessment — transition immediately; never block on TTS or fullscreen
  const handleStartAssessment = () => {
    if (isStartingAssessment) return
    setIsStartingAssessment(true)
    clearQueue()
    setStep('assessment')
    // Fire-and-forget: do not await (some browsers hang on fullscreen promises)
    if (getProctoringConfig().browserLockdown) {
      void enterFullscreen().catch(() => {})
    }
  }

  // Retake photo (back to liveness or capture depending on previous state)
  const retakePhoto = () => {
    setCapturedImage(null)
    setEnrollmentStatus(null)
    setLivenessResult(null)
    enrollmentStarted.current = false

    // If liveness was passed, go directly to capture
    if (livenessResult?.passed) {
      setPhase('capturing')
      speak("Let's try again. Position your face and press capture.")
    } else {
      // Reset and start over with liveness
      setPhase('intro')
      setLivenessAttempts(0)
      speak("Let's start over. Click the button to begin the liveness check.")
    }
  }

  // Loading model state
  if (isLoadingModel) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="glass-card p-8 text-center max-w-md">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Loading Face Detection</h2>
          <p className="text-gray-400">Preparing the AI model for face recognition...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="glass-card p-6 lg:p-8 flex-1">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Face Enrollment</h2>
          <p className="text-gray-400">For secure proctoring during assessment</p>
          {isDetectorReady() && (
            <p className="text-green-400 text-sm mt-1">Face detection ready</p>
          )}
        </div>

        {/* Phase: Intro - Ready to start liveness */}
        {phase === 'intro' && (
          <div className="space-y-4 max-w-md mx-auto">
            <div className="aspect-[4/3] bg-black rounded-lg overflow-hidden">
              <Webcam
                onStream={handleStream}
                showCapture={false}
                className="w-full h-full"
              />
            </div>

            <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-4 space-y-3">
              <h3 className="text-primary-300 font-medium">Face verification steps</h3>
              <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside">
                <li>Listen to Stella&apos;s instructions — wait until she finishes speaking.</li>
                <li>Perform a quick liveness action (blink or nod) when prompted.</li>
                <li>Capture a clear photo of your face for identity verification.</li>
              </ol>
              <p className="text-xs text-gray-500">
                Keep your face centered, use good lighting, and look directly at the camera.
              </p>
              <button
                onClick={() => void startLivenessCheck()}
                disabled={!introReady || isSpeaking || isRunningLiveness}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white py-3 rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {!introReady || isSpeaking ? 'Wait for Stella...' : 'Begin Verification'}
              </button>
            </div>
          </div>
        )}

        {/* Phase: Liveness - Performing liveness check */}
        {phase === 'liveness' && (
          <div className="space-y-4 max-w-md mx-auto">
            <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden">
              <Webcam
                onStream={handleStream}
                showCapture={false}
                className="w-full h-full"
              />

              {/* Challenge overlay */}
              <div className="absolute inset-0 pointer-events-none">
                {isSpeaking ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <p className="text-white text-sm font-medium px-4 text-center">
                      Listen to Stella — no action needed yet
                    </p>
                  </div>
                ) : countdown !== null ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <div className="text-center">
                      <p className="text-white text-sm mb-2">Get ready...</p>
                      <span className="text-6xl font-bold text-primary-400">{countdown}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary-500/90 px-4 py-2 rounded-full text-white font-medium flex items-center gap-2">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      {livenessChallenge ? getChallengeLabel(livenessChallenge) : 'Action'}
                    </div>

                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-black/50 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-primary-500 h-full transition-all duration-100"
                          style={{ width: `${livenessProgress * 100}%` }}
                        />
                      </div>
                      <p className="text-center text-white text-sm mt-2 font-medium">
                        {livenessChallenge ? getChallengeInstruction(livenessChallenge) : 'Follow the instructions'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-400 text-sm text-center font-medium">
                {isSpeaking
                  ? 'Listen to Stella — no action needed yet.'
                  : countdown !== null
                    ? 'Get ready — start the action when the countdown ends.'
                    : livenessChallenge
                      ? getChallengeInstruction(livenessChallenge)
                      : 'Follow the on-screen prompt when ready.'}
              </p>
            </div>
          </div>
        )}

        {/* Phase: Failed - Liveness or enrollment failed */}
        {phase === 'failed' && !capturedImage && (
          <div className="space-y-4 max-w-md mx-auto">
            <div className="aspect-[4/3] bg-black rounded-lg overflow-hidden">
              <Webcam
                onStream={handleStream}
                showCapture={false}
                className="w-full h-full"
              />
            </div>

            {livenessResult && !livenessResult.passed && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-red-400 font-medium">Liveness check failed</span>
                </div>
                <p className="text-sm text-gray-400">
                  {livenessResult.error || `Confidence: ${Math.round(livenessResult.confidence * 100)}%`}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => void startLivenessCheck()}
                disabled={isSpeaking || isRunningLiveness}
                className="flex-1 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white py-3 rounded-lg transition-colors"
              >
                {isSpeaking || isRunningLiveness ? 'Wait...' : 'Try Again'}
              </button>

              {livenessAttempts >= MAX_LIVENESS_ATTEMPTS && (
                <button
                  onClick={skipLiveness}
                  disabled={isSpeaking}
                  className="flex-1 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-600/50 disabled:cursor-not-allowed text-white py-3 rounded-lg transition-colors"
                >
                  {isSpeaking ? 'Wait...' : 'Skip & Capture'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Phase: Capturing - Ready to capture photo */}
        {phase === 'capturing' && !capturedImage && (
          <div className="space-y-4">
            <div className="aspect-[4/3] bg-black rounded-lg overflow-hidden max-w-md mx-auto">
              <Webcam
                onCapture={handleCapture}
                onStream={handleStream}
                showCapture={true}
                className="w-full h-full"
              />
            </div>

            {/* Liveness passed indicator */}
            {livenessResult?.passed && (
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 max-w-md mx-auto">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-400 text-sm">Liveness verified</span>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-4 max-w-md mx-auto space-y-2">
              <h3 className="text-primary-300 font-medium">Capture your photo</h3>
              <p className="text-sm text-gray-300">
                Position your face inside the oval, then press <strong className="text-white">Capture Photo</strong>.
              </p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>Face the camera directly</li>
                <li>Ensure good lighting on your face</li>
                <li>Remove glasses if possible</li>
                <li>Keep a neutral expression</li>
              </ul>
            </div>
          </div>
        )}

        {/* Captured image - enrolling/verifying/complete/failed */}
        {capturedImage && (
          <div className="space-y-4 max-w-md mx-auto">
            {/* Preview captured image */}
            <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden">
              <img
                src={`data:image/jpeg;base64,${capturedImage}`}
                alt="Captured face"
                className="w-full h-full object-cover scale-x-[-1]"
              />

              {/* Loading overlay */}
              {(phase === 'enrolling' || phase === 'verifying') && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-12 h-12 border-3 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-white font-medium">
                      {phase === 'verifying' ? 'Verifying system...' : 'Detecting face...'}
                    </p>
                  </div>
                </div>
              )}

              {/* Success overlay */}
              {phase === 'complete' && enrollmentStatus?.success && (
                <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                  <div className="bg-green-500 text-white px-4 py-2 rounded-full flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Verified
                  </div>
                </div>
              )}
            </div>

            {/* Enrollment status */}
            {enrollmentStatus && (
              <div
                className={`p-4 rounded-lg ${
                  enrollmentStatus.success
                    ? 'bg-green-500/20 border border-green-500/30'
                    : 'bg-red-500/20 border border-red-500/30'
                }`}
              >
                <div className="flex items-center gap-2">
                  {enrollmentStatus.success ? (
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className={enrollmentStatus.success ? 'text-green-400' : 'text-red-400'}>
                    {enrollmentStatus.message}
                  </span>
                </div>
              </div>
            )}

            {/* Start Assessment button - show on success */}
            {phase === 'complete' && enrollmentStatus?.success && (
              <button
                onClick={() => void handleStartAssessment()}
                disabled={isStartingAssessment}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 disabled:cursor-not-allowed text-white py-4 rounded-lg transition-colors text-lg font-semibold flex items-center justify-center gap-3"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {isStartingAssessment ? 'Starting...' : 'Start Assessment'}
              </button>
            )}

            {/* Retake button - only show on failure */}
            {phase === 'failed' && enrollmentStatus && !enrollmentStatus.success && (
              <button
                onClick={retakePhoto}
                disabled={isStartingAssessment}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white py-3 rounded-lg transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  )
}
